import React, { useState } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Dimensions,
  Platform,
  Image,
} from "react-native";
import { LightweightCompanyLogo } from "../../components/LightweightImages";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { resetShiftHandoverState } from "../../store/slices/shiftHandOverSlice";
import LHConfirmationDialog from "../../components/LHConfirmationDialog";
import FontFamily from "../../constants/fonts";
import DLOBreadcrumb, { DLOBreadcrumbItem } from "../../modules/dlo/components/DLOBreadcrumb";
import { useTheme } from "../../theme";

interface ShiftHandOverDetailHeaderProps {
  logoSource: any;
  onBackPress?: () => void;
  onHomePress?: () => void; // Add this prop for home navigation
}

const { width: screenWidth } = Dimensions.get("window");

const ShiftHandOverDetailHeader: React.FC<ShiftHandOverDetailHeaderProps> = ({
  onBackPress,
  onHomePress,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkMode = resolvedTheme === "dark";
  const dispatch = useDispatch();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Track which navigation target confirmation is for
  const [navigationTarget, setNavigationTarget] = useState<'home' | 'back'>('back');

  // Get the selected shift, attendees, and handoverState from Redux store
  const { selectedShift, selectedAttendees, handoverState } = useSelector(
    (state: RootState) => state.shiftHandOver
  );

  // Handle return to dashboard click
  const handleReturnClick = () => {
    // Only show confirmation for 'in-progress' or 'new' statuses
    if (handoverState === "in-progress" || handoverState === "new") {
      setShowConfirmDialog(true);
      setNavigationTarget('back');
    } else {
      // For completed status, just go back immediately
      dispatch(resetShiftHandoverState());

      if (onBackPress) {
        onBackPress();
      }
    }
  };

  // Handle Home navigation (matches PulseCheckHeader)
  const handleHomeClick = () => {
    if (handoverState === "completed") {
      dispatch(resetShiftHandoverState());
      if (onHomePress) onHomePress();
      return;
    }
    if (handoverState === "in-progress" || handoverState === "new") {
      setShowConfirmDialog(true);
      // Set a flag so confirmation knows it's for home
      setNavigationTarget('home');
    } else {
      dispatch(resetShiftHandoverState());
      if (onHomePress) onHomePress();
    }
  };

  const handleConfirm = () => {
    // Reset the shift handover state when returning to dashboard
    dispatch(resetShiftHandoverState());
    setShowConfirmDialog(false);

    if (navigationTarget === 'home' && onHomePress) {
      onHomePress();
    } else if (navigationTarget === 'back' && onBackPress) {
      onBackPress();
    }
  };
  const handleCancel = () => {
    setShowConfirmDialog(false);
  };

  // Breadcrumb items
  const breadcrumbItems: DLOBreadcrumbItem[] = [
    {
      id: 'handover',
      label: 'Shift Handover',
      onPress: () => {
        if (handoverState === "in-progress" || handoverState === "new") {
          setShowConfirmDialog(true);
          setNavigationTarget('back');
        } else {
          dispatch(resetShiftHandoverState());
          if (onBackPress) onBackPress();
        }
      },
    },
    {
      id: 'shift',
      label: selectedShift ? `${selectedShift.displayText} Shift` : 'No shift selected',
      isCurrentPage: true,
    },
  ];

  return (
    <>
      <View
        style={[
          styles.headerRow,
          isDarkMode && { backgroundColor: colors.background.page },
        ]}
      >
        <View
          style={[
            styles.leftGroup,
            isDarkMode && { backgroundColor: colors.background.page },
          ]}
        >
         
          { (Platform.OS === "ios" || Platform.OS === "android") && screenWidth < 600 ? (
            <TouchableOpacity
              style={styles.mobileBackRow}
              onPress={handleReturnClick}
              accessibilityRole="button"
              accessibilityLabel="Back to Shift Handover"
              testID="shift-handover-back-button"
            >
              <Text style={[styles.backIcon, isDarkMode && { color: colors.brand.primary }]}>←</Text>
              <Text style={[styles.backButtonText, isDarkMode && { color: colors.brand.primary }]}>Back to Shift Handover</Text>
            </TouchableOpacity>
          ) : (
            <View style={{ marginBottom: 8 }}>
              <DLOBreadcrumb
                items={breadcrumbItems}
                showHomeIcon={true}
                onHomePress={handleHomeClick}
                style={{ flexDirection: 'row', alignItems: 'center' }}
              />
            </View>
          )}
          {/* Title and Attendees */}
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Text
              style={[styles.title, isDarkMode && { color: colors.text.primary }]}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              Shift Handover & Walk - {selectedShift ? `${selectedShift.displayText} Shift` : "No shift selected"}
            </Text>
          </View>
          <View style={styles.attendeesList}>
            {selectedAttendees.length > 0 ? (
              selectedAttendees.map((attendee, index) => (
                <Text
                  key={attendee.id}
                  style={[styles.attendeeItem, isDarkMode && { color: colors.text.primary }]}
                  numberOfLines={1}
                  ellipsizeMode="tail"
                >
                  {index + 1}. {attendee.displayName} ({attendee.userPrincipalName})
                </Text>
              ))
            ) : (
              <Text
                style={[styles.sectionContent, isDarkMode && { color: colors.text.primary }]}
              >
                No attendees selected
              </Text>
            )}
          </View>
        </View>
      </View>
      <LHConfirmationDialog
        visible={showConfirmDialog}
        message="You have unsaved data, are you sure you want to go back?"
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        useHighContrastMessage={true}
      />
    </>
  );
};

const styles = StyleSheet.create({
  headerRow: {
    width: "100%",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    // backgroundColor: "white",
    // borderBottomWidth: 1,
    // borderBottomColor: "#e9ecef",
    paddingRight: Dimensions.get("window").width < 600 ? 12 : 24,
    paddingVertical: Dimensions.get("window").width < 600 ? 8 : 12,
    minHeight: undefined,
  },
  leftGroup: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontFamily: FontFamily.bold,
    fontSize: Dimensions.get("window").width < 600 ? 18 : 28,
    fontWeight: "700",
    color: "#2D2D2D",
    lineHeight: 32,
    marginBottom: 4,
  },
  attendeesList: {
    fontFamily: FontFamily.regular,
    fontSize: Dimensions.get("window").width < 600 ? 12 : 16,
    color: "#2D2D2D",
    fontWeight: "400",
    marginTop: 2,
    marginBottom: 0,
    lineHeight: 20,
  },
  attendeeItem: {
    fontSize: Dimensions.get("window").width < 600 ? 12 : 16,
    color: "#495057",
    fontWeight: "500",
    lineHeight: 20,
    marginBottom: 2,
  },
  sectionContent: {
    fontSize: Dimensions.get("window").width < 600 ? 12 : 16,
    color: "#495057",
    fontWeight: "500",
  },
  button: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    backgroundColor: "#0066CC",
    marginLeft: 12,
    alignSelf: "flex-start",
  },
  buttonText: {
    color: "white",
    fontWeight: "600",
    marginLeft: 4,
  },
  buttonIcon: {
    color: "white",
    fontWeight: "600",
    fontSize: 18,
  },
  logoContainer: {
    marginRight: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
    logo: {
    height: 36,
    width: 40,
    marginRight: 10,
  },
  backIcon: {
    fontSize: 22,
    color: '#0066CC',
    fontWeight: 'bold',
    marginRight: 6,
  },
  backButtonText: {
    fontSize: 16,
    color: '#0066CC',
    fontWeight: '600',
  },
  mobileBackRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    paddingVertical: 4,
    paddingHorizontal: 2,
  },
});

export default ShiftHandOverDetailHeader;
